import React, { useEffect, useMemo } from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { Button, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import Loader from '../components/Loader';
import {
  listMyRequestsAsUser,
  deleteRequest,
} from '../actions/requestActions';
import { sortingFunctionForDate, sortingFunctionForIsSubmitted } from '../utils/sortingFunctions';
import RequestPhasesAdmin from '../components/RequestPhasesAdmin';
import FilterComponent from '../components/FilterComponent';
import DataTable from 'react-data-table-component';
import { REQUEST_PHASES } from '../constants/RequestPhases';

const RequestListUserScreen = () => {
  const dispatch = useDispatch();

  const requestListMyUser = useSelector(
    (state) => state.requestListMyUser
  );
  const { loading, error, requests } = requestListMyUser;

  const requestDelete = useSelector((state) => state.requestDelete);
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = requestDelete;

  const { userInfo } = useSelector((state) => state.userLogin);

  useEffect(() => dispatch(listMyRequestsAsUser()), [successDelete]);

  const deleteHandler = (id) => {
    if (window.confirm('Are you sure')) {
      dispatch(deleteRequest(id));
    }
  };

  const columns = [
    {
      name: '',
      selector: row => (<LinkContainer to={`/request/${row._id}/view`}>
        <Button variant="light" className="btn-sm">
          <i className="fas fa-external-link-square-alt"></i>
        </Button>
      </LinkContainer>),
      sortable: true,
      wrap: true,
    },
    {
      name: 'ID',
      selector: row => row.ecrId,
      sortable: true,
      wrap: true,
    },
    {
      name: 'TITLE',
      selector: row => row.title,
      sortable: true,
      wrap: true,
    },
    {
      name: 'INITIATED BY',
      selector: row => row.initiatedBy,
      sortable: true,
      wrap: true,
    },
    {
      name: 'ECR CHAMPION',
      selector: row => row.ecrChampion,
      sortable: true,
      wrap: true,
    },
    {
      name: 'PRODUCTS AFFECTED',
      selector: row => row.productsAffected,
      sortable: true,
      wrap: true,
    },
    {
      name: 'IS SUBMITTED',
      selector: row => row.isSubmitted ? (
        <i
          className="fas fa-check"
          style={{ color: 'green' }}
        ></i>
      ) : (
        <i className="fas fa-times" style={{ color: 'red' }}></i>
      ),
      sortable: true,
      sortFunction: sortingFunctionForIsSubmitted,
      wrap: true,
    },
    {
      name: 'STAGE',
      selector: row => REQUEST_PHASES[row.currentStep],
      sortable: true,
      sortFunction: (a, b) => a.currentStep - b.currentStep,
      wrap: true,
    },
    {
      name: 'CREATED AT',
      selector: row => row.createdAt,
      sortFunction: (a, b) => sortingFunctionForDate(a.createdAt, b.createdAt),
      wrap: true,
    },
    {
      name: 'UPDATED AT',
      selector: row => row.updatedAt,
      sortFunction: (a, b) => sortingFunctionForDate(a.updatedAt, b.updatedAt),
      sortable: true,
      wrap: true,
    },
    (userInfo.isAdmin && {
      name: 'ACTIONS',
      selector: row => (
        <>
          <LinkContainer to={`/request/${row._id}/edit`}>
            <Button variant="light" className="btn-sm">
              <i className="fas fa-edit"></i>
            </Button>
          </LinkContainer>
          <Button
            variant="danger"
            className="btn-sm"
            onClick={() => deleteHandler(row._id)}
          >
            <i className="fas fa-trash"></i>
          </Button>
        </>
      ),
      wrap: true,
    })
  ];

  const data = requests?.map(({ _id, ecrId, title, initiatedBy, ecrChampion, productsAffected, isSubmitted, progress: { currentStep }, createdAt, updatedAt }) => (
    {
      _id,
      ecrId: ecrId ?? '',
      title: title.trim(),
      initiatedBy: initiatedBy?.name,
      ecrChampion: ecrChampion?.name,
      productsAffected: productsAffected.length > 0 ? productsAffected.map(p => p.name).join("|| ") : '',
      isSubmitted,
      currentStep,
      createdAt: new Date(createdAt).toLocaleDateString('en-US'),
      updatedAt: new Date(updatedAt).toLocaleDateString('en-US'),
    }
  ))

  const [filterText, setFilterText] = React.useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = React.useState(
    false
  );

  const filteredItems = data?.filter(
    item =>
      JSON.stringify(item)
        .toLowerCase()
        .indexOf(filterText.toLowerCase()) !== -1
  );


  const subHeaderComponent = useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        setResetPaginationToggle(!resetPaginationToggle);
        setFilterText("");
      }
    };

    return (
      <FilterComponent
        onFilter={e => setFilterText(e.target.value)}
        onClear={handleClear}
        filterText={filterText}
      />
    );
  }, [filterText, resetPaginationToggle]);

  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h3>
            CHANGE REQUESTS <br></br>
          </h3>
        </Col>
      </Row>
      <RequestPhasesAdmin className="my-1"></RequestPhasesAdmin>
      {loadingDelete && <Loader />}
      {errorDelete && <Message variant="danger">{errorDelete}</Message>}
      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={filteredItems}
            defaultSortFieldId={2}
            defaultSortAsc={false}
            highlightOnHover
            striped
            responsive
            subHeader
            subHeaderComponent={subHeaderComponent}
            pagination
          />
        </>
      )}
    </>
  );
};

export default RequestListUserScreen;
